import {
  getOfflineBootstrap,
  getOfflineComponentTree,
  listOfflineEquipmentInstances,
  listOfflineRequisitionSpareEquipmentInstances,
  listOfflineSpareEquipmentInstances,
  listOfflineItemCatalog,
  listOfflineLubeOilChart,
  listOfflinePurchaseOrders,
  listOfflineRequisitions,
  listOfflineVendors,
} from "../api/offline";
import type { AuthUser } from "../types/auth";
import type { OfflineSyncEventRecord } from "../types/offline";
import type { VesselId } from "../types/vessel";
import { getSyncMetadata, updateSyncMetadata } from "./indexedDB/syncMetadata";
import { replayPendingRequisitionIntents } from "./syncManager";

interface VesselOfflineSyncResult {
  processedQueueCount: number;
  failedIntentId: string | null;
  completedTiers: string[];
}

function resolveVesselId(user: AuthUser | null | undefined): VesselId | null {
  return user?.vessel_id ?? user?.vessels?.[0]?.id ?? null;
}

function eventId(eventType: OfflineSyncEventRecord["eventType"]): string {
  return `${eventType}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

async function getCacheStore() {
  return import("./indexedDB/cacheStore");
}

async function runTier<T>(
  eventType: OfflineSyncEventRecord["eventType"],
  handler: () => Promise<T>,
): Promise<T> {
  const startedAt = new Date().toISOString();
  const id = eventId(eventType);
  const cacheStore = await getCacheStore();
  await cacheStore.recordSyncEvent({
    id,
    eventType,
    status: "started",
    startedAt,
    completedAt: null,
    message: null,
  });

  try {
    const result = await handler();
    await cacheStore.recordSyncEvent({
      id,
      eventType,
      status: "completed",
      startedAt,
      completedAt: new Date().toISOString(),
      message: null,
    });
    return result;
  } catch (error) {
    await cacheStore.recordSyncEvent({
      id,
      eventType,
      status: "failed",
      startedAt,
      completedAt: new Date().toISOString(),
      message: error instanceof Error ? error.message : "Offline sync tier failed.",
    });
    throw error;
  }
}

export async function runVesselOfflineSync(user: AuthUser | null | undefined): Promise<VesselOfflineSyncResult> {
  const vesselId = resolveVesselId(user);
  if (user?.type !== "crew" || vesselId === null) {
    return { processedQueueCount: 0, failedIntentId: null, completedTiers: [] };
  }

  const cacheStore = await getCacheStore();
  await cacheStore.saveOfflineUserSession({
    key: `user:${user.id}`,
    userId: user.id,
    vesselId,
    userType: user.type,
    cachedAt: new Date().toISOString(),
    expiresAt: null,
  });

  const metadata = await getSyncMetadata();
  const completedTiers: string[] = [];

  try {
    const queueResult = await runTier("queue_replay", async () => replayPendingRequisitionIntents());
    completedTiers.push("queue_replay");
    await updateSyncMetadata({ lastQueueReplayAt: new Date().toISOString(), lastError: null });

    const bootstrapRequired = !metadata.lastReconciliationAt;
    if (bootstrapRequired) {
      const bootstrap = await runTier("full_reconciliation", () =>
        getOfflineBootstrap({ vessel_id: vesselId, page_size: 500 }),
      );
      const syncedAt = bootstrap.data.server_time;
      await cacheStore.cacheRequisitions(bootstrap.data.requisitions, {
        vesselId,
        source: "offline_bootstrap",
        syncedAt,
      });
      await cacheStore.cachePurchaseOrders(bootstrap.data.purchase_orders, {
        vesselId,
        source: "offline_bootstrap",
        syncedAt,
      });
      await cacheStore.cacheVendors(bootstrap.data.vendors, { source: "offline_bootstrap", syncedAt });
      await cacheStore.cacheItemCatalog([...bootstrap.data.item_catalog, ...bootstrap.data.equipment_instances], {
        vesselId,
        source: "offline_bootstrap",
        syncedAt,
      });
      await cacheStore.cacheSpareEquipmentInstances(bootstrap.data.spare_equipment_instances, {
        vesselId,
        source: "offline_bootstrap",
        syncedAt,
      });
      await cacheStore.cacheRequisitionSpareEquipmentInstances(
        bootstrap.data.requisition_spare_equipment_instances,
        {
          vesselId,
          source: "offline_bootstrap",
          syncedAt,
        },
      );
      await cacheStore.cacheComponentTree(bootstrap.data.component_tree, {
        vesselId,
        source: "offline_bootstrap",
        syncedAt,
      });
      await cacheStore.cacheLubeOilChart(bootstrap.data.lube_oil_chart, {
        vesselId,
        source: "offline_bootstrap",
        syncedAt,
      });
      completedTiers.push("full_reconciliation");
      await updateSyncMetadata({
        lastSyncedAt: syncedAt,
        lastReconciliationAt: syncedAt,
        lastRequisitionCacheAt: syncedAt,
        lastPurchaseOrderCacheAt: syncedAt,
        lastVendorCacheAt: syncedAt,
        lastCatalogCacheAt: syncedAt,
        lastComponentTreeCacheAt: syncedAt,
        lastLubeChartCacheAt: syncedAt,
        lastError: null,
      });

      return {
        processedQueueCount: queueResult.processedCount,
        failedIntentId: queueResult.failedIntentId,
        completedTiers,
      };
    }

    const requisitions = await runTier("requisition_delta", () =>
      listOfflineRequisitions({ vessel_id: vesselId, updated_since: metadata.lastRequisitionCacheAt, page_size: 500 }),
    );
    await cacheStore.cacheRequisitions(requisitions.data, { vesselId, source: "delta_sync", syncedAt: requisitions.server_time });
    completedTiers.push("requisition_delta");

    const purchaseOrders = await runTier("purchase_order_delta", () =>
      listOfflinePurchaseOrders({
        vessel_id: vesselId,
        updated_since: metadata.lastPurchaseOrderCacheAt,
        page_size: 500,
      }),
    );
    await cacheStore.cachePurchaseOrders(purchaseOrders.data, {
      vesselId,
      source: "delta_sync",
      syncedAt: purchaseOrders.server_time,
    });
    completedTiers.push("purchase_order_delta");

    const vendors = await runTier("vendor_delta", () =>
      listOfflineVendors({ updated_since: metadata.lastVendorCacheAt, page_size: 500 }),
    );
    await cacheStore.cacheVendors(vendors.data, { source: "delta_sync", syncedAt: vendors.server_time });
    completedTiers.push("vendor_delta");

    const [
      items,
      equipmentInstances,
      spareEquipmentInstances,
      requisitionSpareEquipmentInstances,
      componentTree,
    ] = await Promise.all([
      runTier("catalog_delta", () =>
        listOfflineItemCatalog({ vessel_id: vesselId, updated_since: metadata.lastCatalogCacheAt, page_size: 1000 }),
      ),
      runTier("catalog_delta", () =>
        listOfflineEquipmentInstances({
          vessel_id: vesselId,
          updated_since: metadata.lastCatalogCacheAt,
          page_size: 1000,
        }),
      ),
      runTier("catalog_delta", () =>
        listOfflineSpareEquipmentInstances({
          vessel_id: vesselId,
          page_size: 1000,
        }),
      ),
      runTier("catalog_delta", () =>
        listOfflineRequisitionSpareEquipmentInstances({
          vessel_id: vesselId,
          page_size: 1000,
        }),
      ),
      runTier("component_tree_delta", () =>
        getOfflineComponentTree({ vessel_id: vesselId, updated_since: metadata.lastComponentTreeCacheAt }),
      ),
    ]);
    await cacheStore.cacheItemCatalog([...items.data, ...equipmentInstances.data], {
      vesselId,
      source: "delta_sync",
      syncedAt: items.server_time,
    });
    await cacheStore.cacheSpareEquipmentInstances(spareEquipmentInstances.data, {
      vesselId,
      source: "delta_sync",
      syncedAt: spareEquipmentInstances.server_time,
    });
    await cacheStore.cacheRequisitionSpareEquipmentInstances(
      requisitionSpareEquipmentInstances.data,
      {
        vesselId,
        source: "delta_sync",
        syncedAt: requisitionSpareEquipmentInstances.server_time,
      },
    );
    await cacheStore.cacheComponentTree(componentTree.data, {
      vesselId,
      source: "delta_sync",
      syncedAt: componentTree.server_time,
    });
    completedTiers.push("catalog_delta", "component_tree_delta");

    const lubeChart = await runTier("lube_chart_delta", () =>
      listOfflineLubeOilChart({ vessel_id: vesselId, updated_since: metadata.lastLubeChartCacheAt, page_size: 1000 }),
    );
    await cacheStore.cacheLubeOilChart(lubeChart.data, {
      vesselId,
      source: "delta_sync",
      syncedAt: lubeChart.server_time,
    });
    completedTiers.push("lube_chart_delta");

    const syncedAt = new Date().toISOString();
    await updateSyncMetadata({
      lastSyncedAt: syncedAt,
      lastRequisitionCacheAt: requisitions.server_time,
      lastPurchaseOrderCacheAt: purchaseOrders.server_time,
      lastVendorCacheAt: vendors.server_time,
      lastCatalogCacheAt: items.server_time,
      lastComponentTreeCacheAt: componentTree.server_time,
      lastLubeChartCacheAt: lubeChart.server_time,
      lastError: null,
    });

    return {
      processedQueueCount: queueResult.processedCount,
      failedIntentId: queueResult.failedIntentId,
      completedTiers,
    };
  } catch (error) {
    await updateSyncMetadata({
      lastError: error instanceof Error ? error.message : "Vessel offline sync failed.",
    });
    throw error;
  }
}
